import { computed } from 'vue';
import { GeoPoint } from '@firebase/firestore-types';
import { NumberWithMetric } from '@/types/shared';
import { Truck, Vendor, VendorMaterialStock } from '@/types/app';
import { colors, firebaseEmptyField, metricConversionRates } from '@/constants';
import { DistanceUnits, VolumeAndWeightUnits } from '@/enums';
import { useGoogleMaps } from '@/services';
import { Route } from '@/types';
import { useMetricConverters, geoPointToLatLng, geoPointToLatLngLiteral } from '.';
import {
  getMaterialCost,
  getSubtotalCost,
  getTotalMaterial,
  getTotalCost,
  getTotalTime,
  getTotalTrips,
  getTotalTruckingCost,
  getQuantity,
  getMarkup,
  getSalesTax,
  getHourlyRate,
  getCostPerLoad,
  getCostPerUnit,
  getRoundTripTime,
  formatNum
} from './route-utilities';

export const getRouteDistance = (route: google.maps.DirectionsResult) => {
  return route.routes[0].legs.reduce((accumulator, value) => {
    return accumulator + (value?.distance?.value ?? 0);
  }, 0);
};

export const getRouteDuration = (route: google.maps.DirectionsResult) => {
  return route.routes[0].legs.reduce((accumulator, value) => {
    return accumulator + (value.duration?.value ?? 0);
  }, 0);
};

export const isWithinSearchRadius = (
  a: google.maps.LatLng,
  b: google.maps.LatLng,
  searchRadius: NumberWithMetric<DistanceUnits>
) => {
  const { useGeometry } = useGoogleMaps();
  const { getLinearDistance } = useGeometry();

  const { convertKilometersToMiles, convertMetersToMiles } = useMetricConverters();
  const linearDistanceInMeters = getLinearDistance(a, b);
  const searchRadiusInMiles =
    searchRadius.metric === DistanceUnits.Miles ? searchRadius.number : convertKilometersToMiles(searchRadius.number);

  return convertMetersToMiles(linearDistanceInMeters) < searchRadiusInMiles;
};

export const getVendorDirectionsWithinSearchRadius = (
  vendors: Vendor[],
  origin: GeoPoint,
  searchRadius: NumberWithMetric<DistanceUnits>
) => {
  if (!origin) return Promise.resolve([]);
  const { useDirections } = useGoogleMaps();
  const { getDirections } = useDirections();

  return Promise.all(
    vendors.map(async (vendor, index) => {
      if (!isWithinSearchRadius(geoPointToLatLng(origin), geoPointToLatLng(vendor.location.geoPoint), searchRadius)) {
        return null;
      }

      const directionsRequest: google.maps.DirectionsRequest = {
        origin: geoPointToLatLngLiteral(origin),
        destination: geoPointToLatLngLiteral(vendor.location.geoPoint),
        travelMode: google.maps.TravelMode.DRIVING
      };

      return getDirections(directionsRequest, vendor, colors[index]);
    })
  );
};

export const calculateRouteCostWithTruck = ({
  truck,
  selectedMaterialStock,
  route,
  taxExempt,
  tax,
  doApplyMarkup,
  markup,
  quantity,
  quantityMetric,
  minTime
}: {
  truck: Truck;
  selectedMaterialStock: VendorMaterialStock | undefined;
  route: Route | null | undefined;
  taxExempt: boolean;
  tax: number;
  doApplyMarkup: boolean | undefined;
  markup: number | undefined;
  quantity: number;
  quantityMetric: VolumeAndWeightUnits;
  minTime: number;
}) => {
  const oneWayTripDuration = (route?.directionsResult && getRouteDuration(route.directionsResult) / 60) ?? 0;
  const totalJobDuration =
    Math.ceil(oneWayTripDuration * 2 * (truck.timeMultiplier || 1) + Number(truck.unloadLoadTime)) / 60;

  const conversionRate = computed(() => {
    return selectedMaterialStock?.material.conversionRate === firebaseEmptyField
      ? metricConversionRates.generalCubicYardToImperialTon
      : (selectedMaterialStock?.material.conversionRate as number);
  });

  const findCapacityUnit = () => {
    return quantityMetric === VolumeAndWeightUnits.Tons ? 'Weight' : 'Volume';
  };

  const totalQty = getQuantity(quantity, quantityMetric);
  const totalMarkup = getMarkup(quantity, markup, quantityMetric, doApplyMarkup ?? true);
  const materialCost = getMaterialCost(selectedMaterialStock, quantityMetric, quantity, conversionRate);
  const subtotalCost = getSubtotalCost(
    materialCost,
    totalQty.value,
    quantityMetric,
    selectedMaterialStock,
    conversionRate,
    markup,
    doApplyMarkup ?? true
  );
  const totalTax = getSalesTax(subtotalCost, tax, taxExempt);
  const totalMaterial = getTotalMaterial(subtotalCost, totalQty.value, tax, taxExempt);
  const hourlyRate = getHourlyRate(truck);
  const totalTrips = getTotalTrips(totalQty.value, truck, findCapacityUnit, quantityMetric, conversionRate);

  const roundTripHours = `${Math.floor(totalJobDuration) > 1 ? 'hrs' : 'hr'}`;
  const totalTimeHours = `${Math.floor((totalJobDuration * Math.ceil(totalTrips.value) * 60) / 60) > 1 ? 'hrs' : 'hr'}`;
  const roundTripInHours = `${
    Math.floor(totalJobDuration) !== 0 ? `${Math.floor(totalJobDuration)} ${roundTripHours} ` : ''
  }${Math.ceil((totalJobDuration * 60) % 60)} mins`;

  const totalTimeInMinutes = Math.ceil(totalJobDuration * Math.ceil(totalTrips.value) * 60);
  let newTotalTimeInMinutes = totalTimeInMinutes;
  if (newTotalTimeInMinutes < minTime) {
    newTotalTimeInMinutes = minTime;
  }

  const originalTotalTimeInHours = `${
    Math.floor(totalJobDuration * Math.ceil(totalTrips.value)) !== 0
      ? `${Math.floor(totalJobDuration * Math.ceil(totalTrips.value))} ${totalTimeHours} `
      : ''
  }${Math.ceil((totalJobDuration * Math.ceil(totalTrips.value) * 60) % 60)} mins`;

  const totalTimeInHours = `${
    Math.floor(newTotalTimeInMinutes / 60) !== 0 ? `${Math.floor(newTotalTimeInMinutes / 60)} ${totalTimeHours} ` : ''
  }${Math.ceil(newTotalTimeInMinutes % 60)} mins`;

  const totalTime = getTotalTime(
    totalJobDuration,
    totalTrips,
    roundTripInHours,
    originalTotalTimeInHours,
    totalTimeInHours,
    minTime
  );
  const totalTruckingCost = getTotalTruckingCost(totalTime, truck, totalTimeInHours);
  const totalCost = getTotalCost(totalMaterial, totalTruckingCost);
  const roundTrip = getRoundTripTime(oneWayTripDuration, truck, roundTripInHours);
  const costPerUnit = getCostPerUnit(totalCost, quantity, quantityMetric);
  const costPerLoad = getCostPerLoad(totalCost, totalTrips);

  const calculationTooltip = {
    materialCost,
    roundTrip,
    totalTrips,
    totalTime,
    hourlyRate,
    totalTruckingCost,
    subtotalCost,
    totalCost,
    costPerUnit,
    costPerLoad,
    totalMarkup,
    totalTax,
    totalMaterial,
    roundTripInHours,
    totalTimeInHours,
    totalQty
  };

  const runningCostPerUnit = totalTruckingCost.value / (findCapacityUnit() === 'Volume' ? truck.capacity : truck.weight);
  const finalRate = (totalTruckingCost.value + totalMaterial.value) / quantity;

  return {
    rate: formatNum(runningCostPerUnit),
    finalRate: formatNum(finalRate),
    // Extra whitespace is added for alignment in hover tooltip.
    calculationTooltip
  };
};
